import React from 'react';

import { availablePartnerInsuranceTypes } from 'Containers/App/constants';
import { TextField } from 'Containers/App/types';
import { Spinner } from 'DesignLibrary/atoms';
import Checkbox from 'DesignLibrary/atoms/inputs/Checkbox';
import Text from 'Shared/Text';
import { CoveredMember, CoveredMembers } from 'Types/entities';

import { CoveredMembersContainer, LoadingIndicator } from './styled';
import { InsuranceType } from '../types';

function getMemberType(member) {
  if (member.policyholder) {
    return 'policyholder';
  }

  if (member.dependant) {
    return 'dependent';
  }

  return 'spouse';
}

export interface CoverageOptionsProps {
  coveredMembers: CoveredMembers;
  isLoading: boolean;
  insuranceType: InsuranceType;
  handleMemberSelect: (externalId: string) => void;
  transformedPlan?: any;
}

export const CoverageOptions = ({
  coveredMembers,
  isLoading,
  insuranceType,
  handleMemberSelect,
  transformedPlan,
}: CoverageOptionsProps) => {
  const isPartnersEligible = (): boolean => {
    if (insuranceType === 'ltc') {
      // For LTC the key is has_partner_benefits_life_ltc
      return Boolean(transformedPlan?.has_partner_benefits_life_ltc);
    }
    // For other plans, the key is available_to_partner
    return Boolean(transformedPlan?.available_to_partner);
  };

  const renderOnlyPolicyholder = (policyholder: CoveredMember) => (
    <CoveredMembersContainer>
      <div className="coverage-group" role="group" aria-labelledby="coverage-options-label">
        <Checkbox
          key={`${policyholder.memberId}-checkbox`}
          color="--text-black"
          className="member-checkbox policyholder-checkbox"
          checked
          disabled
          handleChange={() => null}
          testId={`${policyholder.memberId}-checkbox`}
        >
          <Text field="profile_section.member.policyholder_card_header" />
        </Checkbox>
      </div>
    </CoveredMembersContainer>
  );

  if (['std', 'ltd'].includes(insuranceType)) {
    const policyholder = Object.values(coveredMembers).find((i) => i.policyholder) as CoveredMember;

    return renderOnlyPolicyholder(policyholder);
  }

  const field: TextField = availablePartnerInsuranceTypes?.includes(insuranceType)
    ? isPartnersEligible()
      ? 'profile_section.member.domestic_partner_card_header'
      : 'profile_section.member.spouse_label'
    : 'profile_section.member.domestic_partner_card_header';

  return (
    <CoveredMembersContainer>
      {isLoading && (
        <LoadingIndicator>
          <Spinner color="--border-gray" />
        </LoadingIndicator>
      )}
      <div className="coverage-group" role="group" aria-labelledby="coverage-options-label">
        {Object.entries(coveredMembers).map(([externalId, member]) => (
          <Checkbox
            key={`${member.memberId}-checkbox`}
            color="--text-black"
            className={`member-checkbox ${getMemberType(member)}-checkbox`}
            checked={coveredMembers[externalId].covered}
            disabled={member.policyholder}
            handleChange={() => handleMemberSelect(externalId)}
            testId={`${externalId}-checkbox`}
          >
            {member.policyholder && <Text field="profile_section.member.policyholder_card_header" />}
            {!member.policyholder && !member.dependant && <Text field={field} />}
            {member.dependant && <Text field="profile_section.member.dependent_card_header" />}

            {member.dependant && <span className="age-gender">&nbsp; {`(${member.age})`}</span>}
          </Checkbox>
        ))}
      </div>
    </CoveredMembersContainer>
  );
};
